import { containsAmpscript } from "./ampscript";
import { containsZephyr, containsZephyrTags } from "./zephyr";

export type PreviewTemplateLanguage =
  | "LIQUID"
  | "HANDLEBARS"
  | "AMPSCRIPT"
  | "ZEPHYR";

const LIQUID_MARKERS = /\{%|\{\{[^}]*\|\s*[a-z_]+/g;
const HANDLEBARS_MARKERS = /\{\{[#/^]|\{\{else\}\}|\{\{\{/g;
const AMPSCRIPT_CODE = /%%[[=]/;
const LIQUID_OR_HANDLEBARS_OPEN = /\{\{|\{%/;
const PLAIN_OUTPUT = /\{\{/;

/**
 * Picks the template language from the markers the text actually carries, so
 * a send renders correctly whatever ESP it came from. `fallback` (usually the
 * ESP's documented language) decides only when the text is ambiguous, such as
 * a bare `{{ first_name }}` that Liquid and Handlebars read the same way.
 * Returns null when the text has no template syntax at all.
 */
export function detectTemplateLanguage(
  text: string,
  fallback: PreviewTemplateLanguage | null = null,
): PreviewTemplateLanguage | null {
  if (AMPSCRIPT_CODE.test(text)) return "AMPSCRIPT";
  // A bare `%%word%%` string can sit in a Liquid or Handlebars email, so it counts only without their markers.
  if (containsAmpscript(text) && !LIQUID_OR_HANDLEBARS_OPEN.test(text)) {
    return "AMPSCRIPT";
  }
  const liquid = text.match(LIQUID_MARKERS)?.length ?? 0;
  const handlebars = text.match(HANDLEBARS_MARKERS)?.length ?? 0;
  if (liquid > 0 || handlebars > 0) {
    if (liquid === handlebars) {
      return fallback === "HANDLEBARS" ? "HANDLEBARS" : "LIQUID";
    }
    return liquid > handlebars ? "LIQUID" : "HANDLEBARS";
  }
  if (containsZephyr(text)) return "ZEPHYR";
  // Liquid, Handlebars and Zephyr all accept a bare `{{ name }}`, so the ESP decides.
  if (PLAIN_OUTPUT.test(text)) {
    return fallback === "HANDLEBARS" || fallback === "ZEPHYR"
      ? fallback
      : "LIQUID";
  }
  return fallback === "ZEPHYR" && containsZephyrTags(text) ? "ZEPHYR" : null;
}
